import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell, ThirdPane } from '../layout';
import { useProjects } from './catalog';
import type { Summary } from './partners';

export interface Agreement {
  id: number; projectId: number | null; projectName: string | null; partnerKey: string | null; partnerName: string | null; validUntil: string | null;
  title: string; fileName: string; size: number; createdAt: string;
}

const size = (bytes: number) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const today = () => new Date().toLocaleDateString('sv-SE');
const addDays = (iso: string, days: number) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return d.toLocaleDateString('sv-SE'); };
/** Ended, or ending within 30 days. */
const ending = (a: Agreement) => (a.validUntil && a.validUntil <= addDays(today(), 30) ? (a.validUntil < today() ? 'ended' : 'soon') : null);

function useAgreementMessage() {
  const { t } = useI18n();
  return (error: unknown) => {
    const code = error instanceof ApiError ? error.code : '';
    if (code === 'pdf_only') return t('Tik PDF, iki 12 MB.');
    if (code === 'partner_or_project_required') return t('Pasirinkite partnerį arba projektą.');
    return errorText(t, error);
  };
}

async function uploadAgreement(body: FormData) {
  const response = await fetch('api/v1/agreements', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, data?.error?.code ?? 'server_error');
}

interface Option { key: string; name: string; group: 'customer' | 'supplier' | 'other' }

/** Customers and suppliers to link an agreement with: those in Balansai, and customers from the register with no invoices yet. */
function usePartnerOptions() {
  const partners = useQuery({ queryKey: ['partners', 'agreements'], queryFn: () => api.get<{ partners: Summary[] }>('api/v1/partners').then((r) => r.partners) });
  const customers = useQuery({ queryKey: ['customers', 'names'], queryFn: () => api.get<{ customers: { id: number; name: string }[] }>('api/v1/customers').then((r) => r.customers) });
  const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9ąčęėįšųūž]+/g, ' ').trim();
  const options: Option[] = (partners.data ?? []).map((p) => ({ key: p.key, name: p.name, group: p.kind === 'supplier' ? 'supplier' : p.kind === 'customer' || p.kind === 'both' ? 'customer' : 'other' }));
  const known = new Set(options.map((o) => fold(o.name)));
  for (const c of customers.data ?? []) if (!known.has(fold(c.name))) options.push({ key: `c:${c.id}`, name: c.name, group: 'customer' });
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

function PartnerOptions({ options }: { options: Option[] }) {
  const { t } = useI18n();
  const groups: [Option['group'], string][] = [['customer', 'Klientai'], ['supplier', 'Tiekėjai'], ['other', 'Kiti']];
  return <>{groups.map(([g, label]) => {
    const list = options.filter((o) => o.group === g);
    return list.length ? <optgroup key={g} label={t(label)}>{list.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}</optgroup> : null;
  })}</>;
}

export function AgreementsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const message = useAgreementMessage();
  const queryClient = useQueryClient();
  const projects = useProjects();
  const options = usePartnerOptions();
  const [project, setProject] = useState('');
  const [partner, setPartner] = useState('');
  const [fileName, setFileName] = useState('');
  const [editing, setEditing] = useState<Agreement | null>(null);
  const query = new URLSearchParams({ ...(project ? { projectId: project } : {}), ...(partner ? { partnerKey: partner } : {}) }).toString();
  const list = useQuery({ queryKey: ['agreements', query], queryFn: () => api.get<{ agreements: Agreement[] }>(`api/v1/agreements${query ? `?${query}` : ''}`).then((r) => r.agreements) });
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['agreements'] }), queryClient.invalidateQueries({ queryKey: ['partner'] })]);
  const upload = useMutation({
    mutationFn: async (form: HTMLFormElement) => {
      await uploadAgreement(new FormData(form));
      form.reset();
      setFileName('');
    },
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: number) => api.delete(`api/v1/agreements/${id}`), onSuccess: refresh });
  const rows = list.data ?? [];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Dokumentai')} title={t('Sutartys')} text={t('Sutartys su klientais ir tiekėjais, saugiai laikomos ne viešame serverio kataloge.')} />}>
      <div className="agreement-filter-bar">
        <button type="button" className={`agreement-all-filter ${project || partner ? '' : 'active'}`} onClick={() => { setProject(''); setPartner(''); }}>{t('Visi')}</button>
        <label className="agreement-filter-form">{t('Partneris')}<select value={partner} onChange={(e) => setPartner(e.target.value)}><option value="">{t('Visi partneriai')}</option><PartnerOptions options={options} /></select></label>
        {projects.data?.length ? <label className="agreement-filter-form">{t('Projektai')}<select value={project} onChange={(e) => setProject(e.target.value)}><option value="">{t('Visi projektai')}</option>{projects.data.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label> : null}
      </div>
      <div className="agreement-layout">
        <section className="panel-card">
          <div className="card-head"><div><p className="eyebrow">{t('Dokumentai')}</p><h2>{t('Sutarčių failai')}</h2></div><span className="file-count">{rows.length} PDF</span></div>
          <div className="agreement-list">
            {list.isSuccess && !rows.length ? <div className="empty-state compact"><div className="pdf-symbol">PDF</div><h3>{t('Sutarčių dar nėra')}</h3><p>{t('Įkelkite pirmąjį dokumentą dešinėje.')}</p></div> : null}
            {rows.map((a) => <AgreementRow key={a.id} a={a} onEdit={() => setEditing(a)} onRemove={() => { if (confirm(t('Pašalinti „{title}“? PDF failas bus ištrintas visam laikui.', { title: a.title }))) remove.mutate(a.id); }} />)}
          </div>
        </section>
        <ThirdPane className="panel-card upload-panel" label={t('Įkelti sutartį')}>
          <p className="eyebrow">{t('Naujas dokumentas')}</p><h2>{t('Įkelti sutartį')}</h2>
          <p className="muted">{t('Tik PDF, iki 12 MB. Pasirinkite partnerį, projektą arba abu.')}</p>
          {upload.isError ? <Alert kind="error">{message(upload.error)}</Alert> : null}
          <form onSubmit={(e) => { e.preventDefault(); upload.mutate(e.currentTarget); }}>
            <label>{t('Partneris')}<select name="partnerKey" defaultValue={partner} key={`p${partner}`}><option value="">—</option><PartnerOptions options={options} /></select></label>
            {projects.data?.length ? <label>{t('Projektas')}<select name="projectId" defaultValue={project} key={`j${project}`}><option value="">—</option>{projects.data.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label> : null}
            <label>{t('Sutarties pavadinimas')}<input name="title" placeholder={t('Pvz., Paslaugų teikimo sutartis')} /></label>
            <label>{t('Galioja iki')} <small className="muted">({t('nebūtina')})</small><input type="date" name="validUntil" /></label>
            <label className="drop-zone">
              <input className="visually-hidden" type="file" name="file" accept="application/pdf,.pdf" required onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')} />
              <span className="drop-icon">⇧</span><strong>{t('Vilkite PDF čia')}</strong><small>{t('arba spustelėkite ir pasirinkite failą')}</small><em>{fileName || t('Failas nepasirinktas')}</em>
            </label>
            <button className="button button--primary" type="submit" disabled={upload.isPending || !fileName}>{t('Patvirtinti ir įkelti')}</button>
          </form>
        </ThirdPane>
      </div>
      {editing ? <EditAgreement a={editing} options={options} onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </PanelShell>
  );
}

function EndBadge({ a }: { a: Agreement }) {
  const { t } = useI18n();
  const state = ending(a);
  if (!a.validUntil) return null;
  return <span className={`agreement-end${state ? ` is-${state}` : ''}`}>{state === 'ended' ? t('Baigėsi {date}', { date: a.validUntil }) : t('Galioja iki {date}', { date: a.validUntil })}</span>;
}

function AgreementRow({ a, onEdit, onRemove }: { a: Agreement; onEdit: () => void; onRemove: () => void }) {
  const { t } = useI18n();
  return (
    <article className="agreement-row">
      <div className="pdf-symbol">PDF</div>
      <div>
        <h3>{a.title}</h3>
        <p className="agreement-project">
          {a.partnerName ? <strong>{a.partnerName}</strong> : <button type="button" className="text-link" onClick={onEdit}>{t('Susieti su partneriu')}</button>}
          {a.projectName ? ` · ${a.projectName}` : ''} <EndBadge a={a} />
        </p>
        <p>{a.fileName} · {size(a.size)} · {new Date(a.createdAt).toLocaleDateString()}</p>
      </div>
      <div className="agreement-actions">
        <button type="button" className="button button--ghost button--small" onClick={onEdit}>{t('Keisti')}</button>
        <Link className="button button--ghost button--small" to="/view" search={{ type: 'agreement', id: a.id, title: a.title }}>{t('Peržiūrėti')}</Link>
        <a className="button button--ghost button--small" href={`api/v1/agreements/${a.id}/file?download=1`}>{t('Atsisiųsti')}</a>
        <button type="button" className="delete-x" aria-label={t('Pašalinti')} onClick={onRemove}>×</button>
      </div>
    </article>
  );
}

/** Link an agreement to a partner and/or project, rename it, set its end date. */
function EditAgreement({ a, options, onClose, onSaved }: { a: Agreement; options: Option[]; onClose: () => void; onSaved: () => unknown }) {
  const { t } = useI18n();
  const message = useAgreementMessage();
  const projects = useProjects();
  const [partnerKey, setPartnerKey] = useState(a.partnerKey ?? '');
  const [projectId, setProjectId] = useState(a.projectId ? String(a.projectId) : '');
  const [title, setTitle] = useState(a.title);
  const [validUntil, setValidUntil] = useState(a.validUntil ?? '');
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { dialog.current?.showModal(); }, []);
  // A linked partner no longer in the list (e.g. merged) still shows under its saved name.
  const all = a.partnerKey && !options.some((o) => o.key === a.partnerKey) ? [...options, { key: a.partnerKey, name: a.partnerName ?? a.partnerKey, group: 'other' as const }] : options;
  const save = useMutation({
    mutationFn: () => api.patch(`api/v1/agreements/${a.id}`, { partnerKey: partnerKey || null, projectId: projectId ? Number(projectId) : null, title: title.trim() || a.title, validUntil: validUntil || null }),
    onSuccess: async () => { await onSaved(); onClose(); },
  });
  return (
    <dialog className="confirm-dialog" ref={dialog} onClose={onClose}>
      <form className="dialog-card" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <p className="eyebrow">{a.fileName}</p>
        <h2>{t('Sutartis')}</h2>
        {save.isError ? <Alert kind="error">{message(save.error)}</Alert> : null}
        <label>{t('Partneris')}<select value={partnerKey} onChange={(e) => setPartnerKey(e.target.value)} autoFocus><option value="">—</option><PartnerOptions options={all} /></select></label>
        {projects.data?.length || a.projectId ? <label>{t('Projektas')}<select value={projectId} onChange={(e) => setProjectId(e.target.value)}><option value="">—</option>{projects.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label> : null}
        <label>{t('Sutarties pavadinimas')}<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} /></label>
        <label>{t('Galioja iki')}<input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></label>
        <div className="dialog-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>{t('Atšaukti')}</button>
          <button type="submit" className="button button--primary" disabled={save.isPending}>{t('Išsaugoti')}</button>
        </div>
      </form>
    </dialog>
  );
}

/** On a partner's card: its agreements, and uploading one straight to it. */
export function PartnerAgreements({ partnerKey, kind, agreements }: { partnerKey: string; kind: string; agreements: Agreement[] }) {
  const { t } = useI18n();
  const message = useAgreementMessage();
  const queryClient = useQueryClient();
  const upload = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.set('partnerKey', partnerKey);
      body.set('file', file);
      return uploadAgreement(body);
    },
    onSuccess: () => Promise.all([queryClient.invalidateQueries({ queryKey: ['partner'] }), queryClient.invalidateQueries({ queryKey: ['agreements'] })]),
  });
  return (
    <div className="partner-agreements">
      <div className="partner-agreements__head">
        <strong>{t('Sutartys')} ({agreements.length})</strong>
        <label className="button button--ghost button--small">
          {upload.isPending ? t('Įkeliama…') : `+ ${t('Įkelti sutartį')}`}
          <input className="visually-hidden" type="file" accept="application/pdf,.pdf" disabled={upload.isPending} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload.mutate(f); }} />
        </label>
        <Link className="text-link" to="/agreements">{t('Visos sutartys')} →</Link>
      </div>
      {upload.isError ? <Alert kind="error">{message(upload.error)}</Alert> : null}
      {agreements.length ? (
        <ul>
          {agreements.map((a) => (
            <li key={a.id}>
              <Link to="/view" search={{ type: 'agreement', id: a.id, title: a.title }}>{a.title}</Link>
              {a.projectName ? <small className="muted"> · {a.projectName}</small> : null} <EndBadge a={a} />
              <a className="text-link" href={`api/v1/agreements/${a.id}/file?download=1`}>{t('Atsisiųsti')}</a>
            </li>
          ))}
        </ul>
      ) : <p className="muted">{kind === 'supplier' ? t('Sutarčių su šiuo tiekėju dar nėra.') : t('Sutarčių su šiuo partneriu dar nėra.')}</p>}
    </div>
  );
}

/** Apžvalga: agreements that have ended in the last 30 days or end within the next 30. */
export function EndingAgreements() {
  const { t } = useI18n();
  const list = useQuery({ queryKey: ['agreements', ''], queryFn: () => api.get<{ agreements: Agreement[] }>('api/v1/agreements').then((r) => r.agreements) });
  const rows = (list.data ?? []).filter((a) => ending(a) && a.validUntil! >= addDays(today(), -30)).sort((a, b) => a.validUntil!.localeCompare(b.validUntil!));
  if (!rows.length) return null;
  return (
    <section className="panel-card agreements-ending">
      <div className="card-head"><div><p className="eyebrow">{t('Sutartys')}</p><h2>{t('Baigiasi sutartys')}</h2></div><Link to="/agreements">{t('Visos sutartys')} →</Link></div>
      <ul>
        {rows.map((a) => (
          <li key={a.id}>
            <Link to="/view" search={{ type: 'agreement', id: a.id, title: a.title }}>{a.title}</Link>
            {a.partnerName || a.projectName ? <small className="muted"> · {a.partnerName ?? a.projectName}</small> : null} <EndBadge a={a} />
          </li>
        ))}
      </ul>
    </section>
  );
}
